import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getUnbilledRevenue, getUnbilledAgeing, getUnbilledEntry,
  createUnbilledEntry, raiseInvoiceFromUnbilled, reverseUnbilledRevenue,
  getPOs, getPOBalance, postRevenueEntry, approveJournalEntry,
  getUnbilledSummary,
} from '../services/api';
import { formatCurrency, formatDate, StatusBadge } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText, Plus, Zap, RotateCcw, ChevronRight,
  Info, TrendingUp, CheckCircle2, Clock, AlertTriangle, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AppModal, ConfirmModal } from '../components/common/AppModal';
import RevenueAllocationRangeBuilder, { isAllocationValid } from '../components/paymentTerms/RevenueAllocationRangeBuilder';
import EqualMonthlyPreviewPanel from '../components/revenue/EqualMonthlyPreviewPanel';

const DAY_COUNT_OPTIONS = [
  { value: 'ACTUAL_ACTUAL', label: 'Actual / Actual' },
  { value: 'ACTUAL_365',    label: 'Actual / 365' },
  { value: 'ACTUAL_360',    label: 'Actual / 360' },
  { value: 'THIRTY_360',    label: '30 / 360' },
];

// ═══════════════════════════════════════════════════════════════
// LIST
// ═══════════════════════════════════════════════════════════════
export default function UnbilledRevenue() {
  const [entries, setEntries]   = useState([]);
  const [ageing, setAgeing]     = useState(null);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getUnbilledRevenue(), getUnbilledAgeing(), getUnbilledSummary()])
      .then(([e, a, s]) => {
        setEntries(e.data.entries || []);
        setAgeing(a.data);
        setSummary(s.data.summary);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const statusIcon = (s) => {
    if (s === 'OPEN')            return <span className="flex items-center gap-1 text-amber-600 text-xs font-medium"><Clock size={12}/> Open</span>;
    if (s === 'FULLY_INVOICED')  return <span className="flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle2 size={12}/> Invoiced</span>;
    if (s === 'REVERSED')        return <span className="flex items-center gap-1 text-gray-400 text-xs"><RotateCcw size={12}/> Reversed</span>;
    return <StatusBadge status={s}/>;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Unbilled Revenue</h2>
          <p className="text-sm text-gray-500 mt-0.5">Ind AS 115 Scenario 2 — work complete, invoice not yet raised</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <Plus size={15}/> New Entry
        </button>
      </div>

      {/* Accounting treatment explainer */}
      <div className="card p-4 bg-blue-50 border-blue-100">
        <div className="flex items-start gap-3">
          <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0"/>
          <div className="text-sm text-blue-800 space-y-1">
            <p className="font-medium">How Scenario 2 works (Ind AS 115)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
              {[
                { step: 'Step A — Work Complete', entry: 'Dr. Unbilled Revenue\nCr. Deferred Revenue', note: 'Records the contract asset before billing' },
                { step: 'Steps B–N — Monthly Recognition', entry: 'Dr. Deferred Revenue\nCr. Revenue', note: 'Posted each month over the service period' },
                { step: 'Step O — Invoice Raised', entry: 'Dr. Customer A/c\nCr. Unbilled Revenue', note: 'Reclassifies contract asset to trade receivable' },
              ].map(s => (
                <div key={s.step} className="bg-white rounded-lg p-3 border border-blue-100">
                  <div className="font-medium text-xs text-blue-700 mb-1">{s.step}</div>
                  <div className="font-mono text-xs text-gray-700 whitespace-pre-line mb-1">{s.entry}</div>
                  <div className="text-xs text-gray-400">{s.note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Three KPI widgets ─────────────────────────────── */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Widget 1: Total Unbilled Revenue Raised */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'var(--bt-orange-light)' }}>
                <FileText size={15} style={{ color: 'var(--bt-orange)' }}/>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--bt-gray-500)' }}>
                Unbilled Revenue
              </span>
            </div>
            <div className="text-2xl font-bold" style={{ color: 'var(--bt-gray-900)' }}>
              {formatCurrency(summary.total_unbilled_raised)}
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--bt-gray-400)' }}>
              Total raised till date · {summary.entry_count} entries
            </div>
          </div>

          {/* Widget 2: Customer Invoices To Be Raised (unbilled balance) */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: '#FEF3C7' }}>
                <Clock size={15} style={{ color: '#D97706' }}/>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--bt-gray-500)' }}>
                Customer Invoices To Be Raised
              </span>
            </div>
            <div className="text-2xl font-bold" style={{ color: '#D97706' }}>
              {formatCurrency(summary.balance_to_invoice)}
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--bt-gray-400)' }}>
              Unbilled balance · {summary.open_count} open entries
            </div>
          </div>

          {/* Widget 3: Customer Invoices Raised from Unbilled */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: '#F0FDF4' }}>
                <CheckCircle2 size={15} style={{ color: '#16A34A' }}/>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--bt-gray-500)' }}>
                Customer Invoice Raised
              </span>
            </div>
            <div className="text-2xl font-bold" style={{ color: '#16A34A' }}>
              {formatCurrency(summary.total_invoiced_from_unbilled)}
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--bt-gray-400)' }}>
              Converted to invoices · {summary.invoiced_count} entries
            </div>
          </div>
        </div>
      )}

      {/* Entries table */}
      <div className="card overflow-hidden">
        <table className="w-full" style={{tableLayout:'fixed'}}>
          <colgroup>
            <col style={{width:130}}/> {/* Entry # */}
            <col/>                     {/* SO / Customer — flexible */}
            <col style={{width:170}}/> {/* Service Period */}
            <col style={{width:120}}/> {/* Amount */}
            <col style={{width:90}}/>  {/* Status */}
            <col style={{width:140}}/> {/* Customer Invoice */}
            <col style={{width:36}}/>  {/* Arrow */}
          </colgroup>
          <thead>
            <tr>{['Entry #', 'SO / Customer', 'Service Period', 'Amount', 'Status', 'Customer Invoice', ''].map(h =>
              <th key={h} className="table-th">{h}</th>
            )}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="table-td text-center text-gray-400 py-8">Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7} className="table-td text-center py-12">
                <FileText size={32} className="mx-auto text-gray-300 mb-2"/>
                <p className="text-gray-400 font-medium">No unbilled revenue entries</p>
                <p className="text-xs text-gray-400 mt-1">Use this when work is complete but you're waiting to raise the customer invoice.</p>
              </td></tr>
            ) : entries.map(e => (
              <tr key={e.unbilled_id} className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/unbilled-revenue/${e.unbilled_id}`)}>
                <td className="table-td">
                  <span className="font-mono text-sm font-semibold text-primary-600">
                    {e.entry_number || `URE-${e.unbilled_id?.slice(0,6).toUpperCase()}`}
                  </span>
                </td>
                <td className="table-td">
                  <div className="font-medium">{e.customer_name || '—'}</div>
                  <div className="text-xs text-gray-400 font-mono">{e.po_number}</div>
                </td>
                <td className="table-td text-sm text-gray-600">
                  {formatDate(e.service_period_from)} → {formatDate(e.service_period_to)}
                </td>
                <td className="table-td font-semibold">{formatCurrency(e.amount, e.currency)}</td>
                <td className="table-td">{statusIcon(e.status)}</td>
                <td className="table-td text-xs font-mono">
                  {e.converted_invoice_number
                    ? <span className="text-green-600 font-semibold">{e.converted_invoice_number}</span>
                    : <span className="text-gray-300">Not yet raised</span>
                  }
                </td>
                <td className="table-td"><ChevronRight size={15} className="text-gray-300"/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateUnbilledModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }}/>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DETAIL
// ═══════════════════════════════════════════════════════════════
export function UnbilledRevenueDetail() {
  const { id } = useParams();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [showReverseConfirm, setShowReverseConfirm] = useState(false);
  const navigate = useNavigate();
  const { canPostJournal } = useAuth();

  const load = useCallback(() => {
    getUnbilledEntry(id).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleReverse = async () => {
    setReversing(true);
    try {
      await reverseUnbilledRevenue(id, { reason: 'Manual reversal' });
      toast.success('Entry reversed');
      setShowReverseConfirm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setReversing(false); }
  };

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-48 bg-gray-200 rounded-xl"/><div className="h-64 bg-gray-200 rounded-xl"/></div>;
  if (!data) return <div className="text-center text-gray-400 py-12">Entry not found</div>;

  const { entry, journal_entries = [], schedule } = data;
  const isOpen = entry.status === 'OPEN';

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={() => navigate('/unbilled-revenue')} className="hover:text-primary-600">Unbilled Revenue</button>
        <ChevronRight size={14}/>
        <span className="text-gray-900 font-medium">{entry.po_number}</span>
      </div>

      {/* Header */}
      <div className="card p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            {/* Entry number — prominent, like an invoice number */}
            <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Unbilled Revenue Entry</div>
            <h1 className="text-xl font-bold font-mono text-primary-600">
              {entry.entry_number || `URE-${entry.unbilled_id?.slice(0,8).toUpperCase()}`}
            </h1>
            <div className="text-sm text-gray-600 mt-1">
              {entry.customer_name || 'Unknown Customer'} ·
              SO: <strong className="font-mono">{entry.po_number}</strong>
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              Service: <strong>{formatDate(entry.service_period_from)}</strong> → <strong>{formatDate(entry.service_period_to)}</strong>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isOpen && (
              <>
                <button onClick={() => setShowInvoiceModal(true)} className="btn-primary">
                  <Zap size={14}/> Raise Customer Invoice
                </button>
                <button onClick={() => setShowReverseConfirm(true)} disabled={reversing}
                  className="btn-secondary text-red-600 border-red-200">
                  <RotateCcw size={14}/> Reverse
                </button>
              </>
            )}
            {entry.status === 'FULLY_INVOICED' && (
              <button
                onClick={() => navigate(`/invoices/${entry.converted_invoice_id}`)}
                className="flex items-center gap-1.5 text-sm text-green-600 font-medium hover:text-green-800 underline"
              >
                <CheckCircle2 size={16}/> Invoice: <strong className="font-mono">{entry.converted_invoice_number}</strong>
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {[
            { label: 'Entry Number',     value: entry.entry_number || '—', mono: true, orange: true },
            { label: 'Amount',           value: formatCurrency(entry.amount, entry.currency) },
            { label: 'Status',           value: <StatusBadge status={entry.status}/> },
            { label: 'Work/Entry Date',  value: formatDate(entry.delivery_acceptance_date) },
            { label: 'Day Count Conv.',  value: entry.day_count_convention === 'THIRTY_360' ? '30/360' : entry.day_count_convention?.replace(/_/g, '/') },
            { label: 'Expected Invoice', value: formatDate(entry.invoice_expected_date) },
          ].map(f => (
            <div key={f.label}>
              <div className="text-xs text-gray-400 uppercase tracking-wide font-medium">{f.label}</div>
              <div className={`font-semibold mt-0.5 ${f.mono ? 'font-mono' : ''} ${f.orange ? 'text-primary-600' : ''}`}>
                {f.value || '—'}
              </div>
            </div>
          ))}
          {/* Customer invoice link — shown when invoice has been raised */}
          {entry.converted_invoice_number && (
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide font-medium">Customer Invoice</div>
              <button
                onClick={() => navigate(`/invoices/${entry.converted_invoice_id}`)}
                className="font-mono font-semibold text-green-600 hover:text-green-800 underline mt-0.5 block"
              >
                {entry.converted_invoice_number}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Journal Entries */}
      <div className="bt-card">
        <div className="bt-card-header">
          <div className="flex items-center gap-2">
            <FileText size={16} style={{ color: 'var(--bt-orange)' }}/>
            <span className="bt-card-title">Journal Entries</span>
            {journal_entries.some(je => je.status === 'DRAFT') && (
              <span className="bt-badge bt-badge-amber" style={{ fontSize: 10 }}>
                {journal_entries.filter(je => je.status === 'DRAFT').length} pending approval
              </span>
            )}
          </div>
          {canPostJournal && journal_entries.some(je => je.status === 'DRAFT') && (
            <span className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>
              As Accounts Officer, you can post draft entries below
            </span>
          )}
        </div>
        {journal_entries.length === 0 ? (
          <div className="px-5 py-6 text-sm" style={{ color: 'var(--bt-gray-400)' }}>No journal entries recorded yet.</div>
        ) : (
          <table className="w-full" style={{tableLayout:'fixed'}}>
            <colgroup>
              <col style={{width:96}}/>
              <col style={{width:130}}/>
              <col style={{width:155}}/>
              <col style={{width:155}}/>
              <col style={{width:110}}/>
              <col/>
              <col style={{width:80}}/>
              <col style={{width:75}}/>
            </colgroup>
            <thead>
              <tr>{['Date', 'Entry Type', 'Debit', 'Credit', 'Amount', 'Narration', 'Status', 'Action'].map(h =>
                <th key={h} className="table-th">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {journal_entries.map(je => (
                <tr key={je.journal_id}
                  style={{
                    background: je.status === 'DRAFT' ? '#FFFBEB' : je.is_reversal ? '#FEF2F2' : undefined
                  }}>
                  <td className="table-td text-sm">{formatDate(je.entry_date)}</td>
                  <td className="table-td">
                    <span style={{ fontSize: 11, fontWeight: 500, background: 'var(--bt-gray-100)', color: 'var(--bt-gray-600)', padding: '2px 6px', borderRadius: 4 }}>
                      {je.entry_type?.replace(/_/g, ' ')}
                    </span>
                    {je.is_reversal && <span className="ml-1 text-xs" style={{ color: 'var(--bt-red)' }}>(reversal)</span>}
                  </td>
                  <td className="table-td text-sm font-medium">{je.debit_account}</td>
                  <td className="table-td text-sm" style={{ color: 'var(--bt-gray-500)' }}>{je.credit_account}</td>
                  <td className="table-td font-semibold">{formatCurrency(je.amount, je.currency)}</td>
                  <td className="table-td text-xs" style={{ color: 'var(--bt-gray-500)' }}>{je.narration}</td>
                  <td className="table-td">
                    {je.status === 'POSTED'
                      ? <span className="bt-badge bt-badge-green">Posted</span>
                      : <span className="bt-badge bt-badge-amber">Draft</span>
                    }
                  </td>
                  <td className="table-td">
                    {je.status === 'DRAFT' && canPostJournal ? (
                      <PostJournalBtn journalId={je.journal_id} onPosted={load}/>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!canPostJournal && journal_entries.some(je => je.status === 'DRAFT') && (
          <div className="px-5 py-3 text-xs" style={{ background: 'var(--bt-amber-bg)', color: 'var(--bt-amber)', borderTop: '1px solid #FDE68A' }}>
            ⏳ Draft entries are pending review by Accounts Officer before posting to ledger.
          </div>
        )}
      </div>

      {/* Revenue Recognition Schedule */}
      {schedule && (
        <div className="card">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <TrendingUp size={16} className="text-primary-500"/>
            <h3 className="font-semibold">Revenue Recognition Schedule</h3>
            <span className="text-xs text-gray-400 ml-auto">
              {schedule.entries?.filter(e => e.status === 'POSTED').length} of {schedule.entries?.length} periods posted
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" style={{tableLayout:'fixed'}}>
              <colgroup>
                <col style={{width:100}}/>
                <col style={{width:55}}/>
                <col style={{width:90}}/>
                <col style={{width:120}}/>
                <col style={{width:140}}/>
                <col style={{width:90}}/>
                <col style={{width:70}}/>
              </colgroup>
              <thead>
                <tr>{['Period', 'Days', 'Proration %', 'Amount', 'Accounting Period', 'Status', 'Action'].map(h =>
                  <th key={h} className="table-th text-xs">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {schedule.entries?.map(e => (
                  <tr key={e.entry_id} className={e.status === 'POSTED' ? 'bg-green-50/40' : e.status === 'REVERSED' ? 'opacity-40' : 'hover:bg-gray-50'}>
                    <td className="table-td font-medium text-sm">{e.period_label}</td>
                    <td className="table-td text-xs text-gray-500">{e.days_in_period}</td>
                    <td className="table-td text-xs text-gray-500">{(parseFloat(e.proration_factor) * 100).toFixed(2)}%</td>
                    <td className="table-td font-semibold">{formatCurrency(e.recognised_amount, entry.currency)}</td>
                    <td className="table-td text-xs font-mono text-gray-500">{e.accounting_period}</td>
                    <td className="table-td">
                      {e.status === 'POSTED'
                        ? <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle2 size={11}/> Posted</span>
                        : e.status === 'REVERSED'
                        ? <span className="text-xs text-gray-400">Reversed</span>
                        : <span className="text-xs text-amber-600">Pending</span>}
                    </td>
                    <td className="table-td">
                      {e.status === 'PENDING' && isOpen && (
                        <PostEntryButton
                          scheduleId={schedule.schedule_id}
                          periodMonth={e.period_month}
                          onPosted={load}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 bg-gray-50 border-t text-xs text-gray-500">
            <strong>Dr. Deferred Revenue / Cr. Revenue</strong> — post each period at month-end close.
          </div>
        </div>
      )}

      {showInvoiceModal && (
        <RaiseInvoiceModal
          entry={entry}
          onClose={() => setShowInvoiceModal(false)}
          onCreated={() => { setShowInvoiceModal(false); load(); toast.success('Customer invoice raised successfully'); }}
        />
      )}
      {showReverseConfirm && (
        <ConfirmModal
          title="Reverse Unbilled Entry"
          message="Reverse this unbilled entry? This will also cancel the recognition schedule."
          confirmLabel="Reverse"
          onConfirm={handleReverse}
          onClose={() => setShowReverseConfirm(false)}
          loading={reversing}
          danger
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CREATE UNBILLED ENTRY MODAL
// ═══════════════════════════════════════════════════════════════
function CreateUnbilledModal({ onClose, onCreated }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    po_id: '', amount: '', currency: 'INR',
    entry_date: today,
    service_period_from: today,
    service_period_to: '',
    day_count_convention: 'ACTUAL_ACTUAL',
    apportionment_method: 'EQUAL_MONTHLY',
    notes: '', expected_invoice_date: '',
  });
  const [allocationRanges, setAllocationRanges] = useState([
    { range_start_date: today, range_end_date: '', allocation_percentage: '65', sequence_order: 1 },
    { range_start_date: '', range_end_date: '', allocation_percentage: '35', sequence_order: 2 },
  ]);
  const [pos, setPOs]           = useState([]);
  const [poBalance, setPOBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [preview, setPreview]   = useState(null);

  useEffect(() => {
    getPOs().then(r => setPOs(r.data.purchase_orders || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.apportionment_method !== 'DAY_COUNT') { setPreview(null); return; }
    if (!form.service_period_from || !form.service_period_to) { setPreview(null); return; }
    const start = new Date(form.service_period_from);
    const end   = new Date(form.service_period_to);
    if (end <= start) { setPreview(null); return; }
    const months = [];
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= endMonth) {
      months.push(new Date(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    setPreview(months.map(m => ({
      label: m.toLocaleString('default', { month: 'short', year: 'numeric' }),
      amount: null,
    })));
  }, [form.service_period_from, form.service_period_to, form.apportionment_method]);

  useEffect(() => {
    if (form.apportionment_method !== 'CUSTOM_PERIOD_ALLOCATION') return;
    if (!form.service_period_from || !form.service_period_to) return;
    setAllocationRanges([
      { range_start_date: form.service_period_from, range_end_date: form.service_period_to, allocation_percentage: '65', sequence_order: 1 },
      { range_start_date: form.service_period_from, range_end_date: form.service_period_to, allocation_percentage: '35', sequence_order: 2 },
    ]);
  }, [form.service_period_from, form.service_period_to, form.apportionment_method]);

  const customAllocationOk = form.apportionment_method !== 'CUSTOM_PERIOD_ALLOCATION'
    || isAllocationValid(form.service_period_from, form.service_period_to, allocationRanges);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePOChange = async (poId) => {
    set('po_id', poId);
    const po = pos.find(p => p.po_id === poId);
    if (po?.currency) set('currency', po.currency);

    if (!poId) { setPOBalance(null); set('amount', ''); return; }

    // Load balance for selected PO
    setBalanceLoading(true);
    try {
      const { data } = await getPOBalance(poId);
      setPOBalance(data);
      // Pre-fill amount with available balance
      set('amount', data.available_balance > 0 ? data.available_balance.toFixed(2) : '0');
      if (data.currency) set('currency', data.currency);
    } catch {
      setPOBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const amountNum        = parseFloat(form.amount) || 0;
  const availableBalance = poBalance?.available_balance ?? null;
  const amountExceeds    = availableBalance !== null && amountNum > availableBalance + 0.01;

  const handleSave = async () => {
    if (!form.po_id)              return toast.error('Select a Sales Order');
    if (!form.amount || amountNum <= 0) return toast.error('Amount is required');
    if (amountExceeds)            return toast.error(`Amount exceeds available balance of ${formatCurrency(availableBalance, form.currency)}`);
    if (!form.service_period_from || !form.service_period_to) return toast.error('Service period dates are required');
    if (!customAllocationOk) return toast.error('Fix custom allocation ranges before saving');
    setSaving(true);
    try {
      const payload = { ...form, amount: amountNum };
      if (form.apportionment_method === 'CUSTOM_PERIOD_ALLOCATION') {
        payload.allocation_ranges = allocationRanges.map((r, i) => ({
          ...r,
          sequence_order: i + 1,
          allocation_percentage: parseFloat(r.allocation_percentage),
        }));
      }
      const result = await createUnbilledEntry(payload);
      toast.success(result.data.message || 'Unbilled entry created');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      title="Record Unbilled Revenue (Ind AS 115 — Scenario 2)"
      subtitle="Work complete but invoice not yet raised to customer"
      onClose={onClose}
      maxWidth={672}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving || amountExceeds || (poBalance && poBalance.available_balance <= 0) || !customAllocationOk}
            className="btn-primary flex-1 justify-center">
            {saving ? 'Recording…' : 'Record Unbilled Revenue Entry'}
          </button>
        </>
      )}
    >
        <div className="space-y-4">
        <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800">
          <strong>Journal entry (Step A):</strong>
          <div className="mt-1.5 font-mono">
            Dr. Unbilled Revenue A/c &nbsp;&nbsp;₹ {form.amount || 'X'}<br/>
            &nbsp;&nbsp;&nbsp;Cr. Deferred Revenue A/c &nbsp;&nbsp;&nbsp;₹ {form.amount || 'X'}
          </div>
        </div>

        <div className="space-y-4">
          {/* Sales Order selection */}
          <div>
            <label className="label">Sales Order <span className="text-red-500">*</span></label>
            <select className="select" value={form.po_id} onChange={e => handlePOChange(e.target.value)} required>
              <option value="">— Select Sales Order —</option>
              {pos.map(p => (
                <option key={p.po_id} value={p.po_id}>
                  {p.po_number} — {p.buyer_name || '?'} — {formatCurrency(p.po_value, p.currency)}
                </option>
              ))}
            </select>
          </div>

          {/* SO Balance breakdown — shown after SO selected */}
          {form.po_id && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                SO Balance
              </div>
              {balanceLoading ? (
                <div className="px-3 py-3 text-xs text-gray-400 animate-pulse">Loading balance…</div>
              ) : poBalance ? (
                <>
                  <div className="grid grid-cols-4 divide-x divide-gray-100">
                    {[
                      { label: 'SO Value',   value: poBalance.po_value,         cls: 'text-gray-900' },
                      { label: 'Invoiced',   value: poBalance.invoiced_amount,   cls: 'text-amber-600' },
                      { label: 'Unbilled',   value: poBalance.unbilled_amount,   cls: 'text-blue-600' },
                      { label: 'Available',  value: poBalance.available_balance, cls: poBalance.available_balance > 0 ? 'text-green-600' : 'text-red-500' },
                    ].map(col => (
                      <div key={col.label} className="px-3 py-2.5 text-center">
                        <div className="text-xs text-gray-400">{col.label}</div>
                        <div className={`font-semibold text-sm mt-0.5 ${col.cls}`}>
                          {formatCurrency(col.value, poBalance.currency)}
                        </div>
                      </div>
                    ))}
                  </div>
                  {poBalance.available_balance <= 0 && (
                    <div className="px-3 py-2 bg-red-50 border-t border-red-100 text-xs text-red-600">
                      ⚠ No available balance — full SO value is already invoiced or recorded as unbilled
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">
                Amount <span className="text-red-500">*</span>
                {availableBalance !== null && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">
                    max: {formatCurrency(availableBalance, form.currency)}
                  </span>
                )}
              </label>
              <input type="number" step="0.01" className={`input ${amountExceeds ? 'border-red-400 bg-red-50' : ''}`}
                value={form.amount} onChange={e => set('amount', e.target.value)}
                max={availableBalance ?? undefined} placeholder="0.00"/>
              {amountExceeds && (
                <p className="text-xs text-red-500 mt-1">
                  Exceeds available balance of {formatCurrency(availableBalance, form.currency)}
                </p>
              )}
            </div>
            <div>
              <label className="label">Currency</label>
              <select className="select" value={form.currency} onChange={e => set('currency', e.target.value)}>
                {['INR','USD','EUR','GBP','AED','SGD'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Work Completion Date</label>
              <input type="date" className="input" value={form.entry_date} onChange={e => set('entry_date', e.target.value)}/>
            </div>
            <div>
              <label className="label">Recognition Start <span className="text-red-500">*</span></label>
              <input type="date" className="input" value={form.service_period_from}
                onChange={e => set('service_period_from', e.target.value)}/>
            </div>
            <div>
              <label className="label">Recognition End <span className="text-red-500">*</span></label>
              <input type="date" className="input" value={form.service_period_to}
                onChange={e => set('service_period_to', e.target.value)} min={form.service_period_from}/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Apportionment</label>
              <select className="select" value={form.apportionment_method}
                onChange={e => set('apportionment_method', e.target.value)}>
                <option value="EQUAL_MONTHLY">Equal monthly</option>
                <option value="DAY_COUNT">Day count convention</option>
                <option value="CUSTOM_PERIOD_ALLOCATION">Custom period allocation</option>
              </select>
            </div>
            {form.apportionment_method === 'DAY_COUNT' && (
            <div>
              <label className="label">Day Count Convention</label>
              <select className="select" value={form.day_count_convention}
                onChange={e => set('day_count_convention', e.target.value)}>
                {DAY_COUNT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            )}
          </div>

          {form.apportionment_method === 'CUSTOM_PERIOD_ALLOCATION' && (
            <RevenueAllocationRangeBuilder
              serviceFrom={form.service_period_from}
              serviceTo={form.service_period_to}
              totalAmount={amountNum}
              ranges={allocationRanges}
              setRanges={setAllocationRanges}
            />
          )}

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="label">Expected Invoice Date</label>
              <input type="date" className="input" value={form.expected_invoice_date}
                onChange={e => set('expected_invoice_date', e.target.value)}/>
            </div>
          </div>

          {form.apportionment_method === 'EQUAL_MONTHLY' && amountNum > 0 && (
            <EqualMonthlyPreviewPanel
              serviceFrom={form.service_period_from}
              serviceTo={form.service_period_to}
              totalAmount={amountNum}
              currency={form.currency}
            />
          )}

          {form.apportionment_method === 'DAY_COUNT' && preview && (
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-xs font-semibold text-gray-500 mb-1.5">{preview.length} recognition periods:</p>
              <div className="flex flex-wrap gap-1.5">
                {preview.map(p => (
                  <span key={p.label} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-600">
                    {p.label}{p.amount != null ? ` · ${formatCurrency(p.amount, form.currency)}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="label">Notes</label>
            <input className="input" value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="e.g. Annual subscription delivered, billing delayed per customer request"/>
          </div>
        </div>
        </div>
    </AppModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// RAISE INVOICE MODAL (Step O)
// ═══════════════════════════════════════════════════════════════
function RaiseInvoiceModal({ entry, onClose, onCreated }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ invoice_date: today, due_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await raiseInvoiceFromUnbilled(entry.unbilled_id, form);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to raise invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      title="Raise Customer Invoice (Step O)"
      subtitle={`${entry.customer_name} — ${formatCurrency(entry.amount, entry.currency)}`}
      onClose={onClose}
      maxWidth={512}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary flex-1 justify-center">
            {saving ? 'Raising Invoice…' : 'Raise Customer Invoice'}
          </button>
        </>
      )}
    >
        {/* Step O journal */}
        <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-xl text-xs text-green-800">
          <strong>Journal entry that will be posted (Step O):</strong>
          <div className="mt-1.5 font-mono">
            Dr. Customer A/c (Trade Receivable) &nbsp;₹ {entry.amount}<br/>
            &nbsp;&nbsp;&nbsp;Cr. Unbilled Revenue A/c &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;₹ {entry.amount}
          </div>
          <div className="mt-1.5 text-green-700">
            Unbilled Revenue (Contract Asset) reclassified to Trade Receivable.
            Revenue recognition schedule continues unchanged.
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Invoice Date</label>
              <input type="date" className="input" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)}/>
            </div>
            <div>
              <label className="label">Due Date</label>
              <input type="date" className="input" value={form.due_date} onChange={e => set('due_date', e.target.value)} min={form.invoice_date}/>
            </div>
          </div>
          <div>
            <label className="label">Invoice Notes</label>
            <input className="input" value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Optional notes on the customer invoice"/>
          </div>
        </div>
    </AppModal>
  );
}

// ── Post Entry Button (inline for unbilled revenue schedule) ─────
function PostEntryButton({ scheduleId, periodMonth, onPosted }) {
  const [posting, setPosting] = useState(false);
  const handlePost = async () => {
    setPosting(true);
    try {
      await postRevenueEntry(scheduleId, { period_month: periodMonth });
      toast.success('Period posted — Dr. Deferred Revenue / Cr. Revenue');
      onPosted();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post');
    } finally { setPosting(false); }
  };
  return (
    <button onClick={handlePost} disabled={posting}
      className="text-xs px-2.5 py-1 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200 font-medium">
      {posting ? 'Posting…' : 'Post'}
    </button>
  );
}

// ── Post Journal Button ──────────────────────────────────────────
function PostJournalBtn({ journalId, onPosted }) {
  const [posting, setPosting] = useState(false);
  const handle = async () => {
    setPosting(true);
    try {
      await approveJournalEntry(journalId);
      toast.success('Journal entry posted');
      onPosted();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post');
    } finally { setPosting(false); }
  };
  return (
    <button onClick={handle} disabled={posting}
      style={{
        fontSize: 11, padding: '3px 10px', borderRadius: 6, fontWeight: 600,
        background: 'var(--bt-orange)', color: '#fff', border: 'none', cursor: 'pointer',
        opacity: posting ? 0.7 : 1,
      }}>
      {posting ? 'Posting…' : 'Post'}
    </button>
  );
}
